import React, { useEffect, useState } from 'react';
import { Modal, Radio, Input, DatePicker, Button, message } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { Headset, X, Phone, PhoneOff, Delete, Minus, PhoneIncoming, Pause, Play, Mic, MicOff } from 'lucide-react';
import {
  AGENT_STATUS, OUTCOME_LABEL, customerByPhone, fmtDuration, upcomingCallbacks, useCallCenter, type Outcome,
} from '../callCenterStore';
import { softphone, talkSeconds, useSoftphone } from '../softphone';
import { CARESOFT_ENABLED, cs, startCareSoft, useCareSoft } from '../caresoft';

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];

/** Bộ đếm giây — chỉ re-render khi đang có cuộc gọi */
function useTick(active: boolean) {
  const [, setT] = useState(0);
  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => setT(x => x + 1), 1000);
    return () => clearInterval(t);
  }, [active]);
}

function WrapupModal() {
  const ses = useSoftphone();
  const s = useCallCenter();
  const open = ses.phase === 'wrapup';
  const customer = open ? customerByPhone(s, ses.phone) : null;
  const [outcome, setOutcome] = useState<Outcome>('answered');
  const [note, setNote] = useState('');
  const [callback, setCallback] = useState<Dayjs | null>(null);
  const [newName, setNewName] = useState('');

  useEffect(() => {
    if (!open) return;
    setOutcome(ses.outcome || 'answered');
    setNote('');
    setCallback(null);
    setNewName('');
  }, [open, ses.outcome]);

  const talk = talkSeconds(ses);
  const save = (withDetails: boolean) => {
    // Quy trình: cuộc nghe máy phải có nội dung trao đổi. "Bỏ qua" vẫn lưu nhưng bị đánh dấu "Thiếu nội dung" để bổ sung sau.
    if (withDetails && outcome === 'answered' && !note.trim()) { message.warning('Nhập nội dung trao đổi với khách'); return; }
    softphone.finish(withDetails
      ? { outcome, note, callbackAt: callback ? callback.valueOf() : null, newCustomerName: newName }
      : { outcome: ses.outcome || 'answered' });
  };

  return (
    <Modal open={open} closable={false} maskClosable={false} keyboard={false} width={480} zIndex={1100}
      title={<span>Kết quả cuộc gọi · {customer?.name || 'Số máy lạ'} <span className="text-gray-400 font-normal">({ses.phone})</span></span>}
      footer={[
        <Button key="skip" onClick={() => save(false)}>Bỏ qua</Button>,
        <Button key="save" type="primary" onClick={() => save(true)}>Lưu kết quả</Button>,
      ]}>
      <div className="space-y-4 pt-1">
        <div className="text-xs text-gray-500">
          {ses.direction === 'out' ? 'Gọi đi' : 'Gọi đến'} · {dayjs(ses.startedAt).format('HH:mm DD/MM')} · Đàm thoại {fmtDuration(talk)}
        </div>
        <div>
          <div className="text-sm font-medium mb-1.5">Kết quả</div>
          <Radio.Group value={outcome} onChange={e => setOutcome(e.target.value)} optionType="button" buttonStyle="solid"
            options={(Object.keys(OUTCOME_LABEL) as Outcome[]).map(k => ({ value: k, label: OUTCOME_LABEL[k], disabled: k === 'answered' && talk === 0 }))} />
        </div>
        <div>
          <div className="text-sm font-medium mb-1.5">Nội dung trao đổi</div>
          <Input.TextArea autoFocus value={note} onChange={e => setNote(e.target.value)} autoSize={{ minRows: 3, maxRows: 6 }}
            placeholder={outcome === 'answered' ? 'Khách quan tâm gì, đã tư vấn gì, bước tiếp theo...' : 'Ghi chú (không bắt buộc)'} />
        </div>
        <div>
          <div className="text-sm font-medium mb-1.5">Hẹn gọi lại</div>
          <DatePicker showTime={{ format: 'HH:mm' }} format="HH:mm DD/MM/YYYY" className="w-full" value={callback} onChange={setCallback}
            placeholder="Không hẹn" disabledDate={d => d.isBefore(dayjs(), 'day')}
            presets={[
              { label: 'Sau 1 giờ', value: dayjs().add(1, 'hour').startOf('minute') },
              { label: 'Sáng mai 9:00', value: dayjs().add(1, 'day').hour(9).minute(0).second(0) },
              { label: 'Chiều mai 15:00', value: dayjs().add(1, 'day').hour(15).minute(0).second(0) },
              { label: 'Tuần sau', value: dayjs().add(7, 'day').hour(9).minute(0).second(0) },
            ]} />
        </div>
        {!customer && (
          <div>
            <div className="text-sm font-medium mb-1.5">Lưu số này thành khách hàng</div>
            <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Nhập tên khách (bỏ trống nếu không tạo)" />
          </div>
        )}
      </div>
    </Modal>
  );
}

/** Dòng trạng thái kết nối tổng đài CareSoft (chỉ hiện khi bật REACT_APP_CARESOFT) */
function CareSoftBar() {
  const c = useCareSoft();
  if (!CARESOFT_ENABLED) return null;
  const live = c.mode === 'ready' && c.voiceHere;
  return (
    <div className={`px-3 py-1.5 text-[11px] flex items-center justify-between gap-2 ${live ? 'bg-green-50 text-green-700' : c.mode === 'error' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}`}>
      <span className="truncate">
        {c.mode === 'connecting' ? 'Đang kết nối tổng đài…'
          : c.mode === 'error' ? `${c.error || 'Lỗi tổng đài'} · đang mô phỏng`
            : live ? `Tổng đài CareSoft đã kết nối${c.ipphone ? ` · máy lẻ ${c.ipphone}` : ''}` : 'Tab khác đang giữ tổng đài'}
      </span>
      {c.mode === 'ready' && !c.voiceHere && <button type="button" onClick={() => cs.enableHere()} className="font-semibold underline cursor-pointer shrink-0">Dùng ở tab này</button>}
      {c.mode === 'error' && <button type="button" onClick={() => startCareSoft(true)} className="font-semibold underline cursor-pointer shrink-0">Thử lại</button>}
    </div>
  );
}

/** Softphone nổi góc phải: bàn phím, đang gọi, cuộc gọi đến, ghi kết quả */
export default function Softphone() {
  const ses = useSoftphone();
  const s = useCallCenter();
  const c = useCareSoft();
  const live = c.mode === 'ready' && c.voiceHere;
  useEffect(() => { startCareSoft(); }, []);
  const inCall = ses.phase === 'calling' || ses.phase === 'connected';
  useTick(inCall || ses.phase === 'ringing');
  const customer = ses.phone ? customerByPhone(s, ses.phone) : null;
  const status = AGENT_STATUS[s.agentStatus];
  const dialerMatch = ses.phase === 'idle' && ses.dialer.replace(/\D/g, '').length >= 9 ? customerByPhone(s, ses.dialer) : null;
  const pendingCb = customer ? upcomingCallbacks(s, customer.phones)[0] : null;

  if (!ses.open) {
    const live = ses.phase !== 'idle';
    return (
      <>
        <button type="button" onClick={() => softphone.openDialer()}
          className={`fixed bottom-6 right-6 z-50 h-12 rounded-full shadow-lg flex items-center gap-2 px-4 text-white font-semibold text-sm cursor-pointer transition hover:scale-105 ${live ? 'bg-green-600 animate-pulse' : 'bg-gray-900'}`}
          title="Mở tổng đài">
          <Headset size={18} />
          {live ? (ses.phase === 'ringing' ? 'Cuộc gọi đến…' : fmtDuration(talkSeconds(ses))) : 'Tổng đài'}
          <span className={`w-2 h-2 rounded-full ${status.dot}`} />
        </button>
        <WrapupModal />
      </>
    );
  }

  return (
    <>
      <div className="fixed bottom-6 right-6 w-72 bg-white dark:bg-gray-900 rounded-xl shadow-[0_10px_40px_-10px_rgba(0,0,0,0.35)] border border-gray-200 dark:border-gray-700 z-50 flex flex-col overflow-hidden">
        <div className="bg-gray-900 text-white px-3 py-2.5 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Headset size={16} className="text-blue-400" />
            <span className="font-semibold text-sm">Tổng đài</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="flex items-center gap-1.5 text-[11px] text-gray-300"><span className={`w-2 h-2 rounded-full ${status.dot}`} />{status.label}</span>
            <button type="button" onClick={() => softphone.minimize()} className="text-gray-400 hover:text-white cursor-pointer" title={ses.phase === 'idle' ? 'Đóng' : 'Thu nhỏ (cuộc gọi vẫn tiếp tục)'}>
              {ses.phase === 'idle' ? <X size={16} /> : <Minus size={16} />}
            </button>
          </div>
        </div>
        <CareSoftBar />

        {ses.phase === 'idle' || ses.phase === 'wrapup' ? (
          <div className="p-4 flex flex-col items-center">
            <input type="tel" value={ses.dialer} onChange={e => softphone.setDialer(e.target.value)} placeholder="Nhập số điện thoại..."
              onKeyDown={e => { if (e.key === 'Enter') softphone.dial(ses.dialer); }}
              className="w-full text-center text-lg font-bold text-gray-800 dark:text-gray-100 border-b-2 border-gray-200 dark:border-gray-700 focus:border-blue-500 focus:outline-none pb-2 bg-transparent" />
            <div className="h-5 text-xs mt-1 mb-2 text-gray-500 truncate max-w-full">{dialerMatch ? <>Khách hàng: <b className="text-gray-700 dark:text-gray-200">{dialerMatch.name}</b></> : ''}</div>
            {live && c.calloutServices.length > 1 && (
              <select value={c.calloutId} onChange={e => cs.setCalloutId(e.target.value)} title="Dịch vụ gọi ra"
                className="w-full mb-3 text-xs border border-gray-200 dark:border-gray-700 rounded px-2 py-1 bg-transparent text-gray-700 dark:text-gray-200">
                {c.calloutServices.map(x => <option key={x.callout_id} value={x.callout_id}>{x.descriptions || x.callout_id}</option>)}
              </select>
            )}
            <div className="grid grid-cols-3 gap-2.5 w-full px-2 mb-4">
              {KEYS.map(k => (
                <button key={k} type="button" onClick={() => softphone.setDialer(ses.dialer + k)} className="h-10 rounded bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 font-semibold text-gray-700 dark:text-gray-200 cursor-pointer">{k}</button>
              ))}
            </div>
            <div className="flex items-center gap-6">
              <span className="w-10" />
              <button type="button" onClick={() => softphone.dial(ses.dialer)} disabled={!ses.dialer.trim()}
                className="w-12 h-12 rounded-full bg-green-500 hover:bg-green-600 disabled:opacity-40 disabled:cursor-not-allowed text-white flex items-center justify-center shadow cursor-pointer" title="Gọi">
                <Phone size={20} />
              </button>
              <button type="button" onClick={() => softphone.setDialer(ses.dialer.slice(0, -1))} className="w-10 h-10 rounded-full text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-center cursor-pointer" title="Xoá">
                <Delete size={18} />
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6 flex flex-col items-center bg-gray-900 text-white">
            <div className="text-gray-400 text-xs mb-1 flex items-center gap-1">
              {ses.phase === 'ringing' ? <><PhoneIncoming size={12} /> Cuộc gọi đến…</> : ses.phase === 'calling' ? 'Đang kết nối…' : 'Đang đàm thoại'}
            </div>
            <div className="text-xl font-bold">{ses.phone}</div>
            <div className="text-xs text-gray-300 mb-1">{customer?.name || 'Số máy lạ'}</div>
            {customer && <div className="text-[11px] text-gray-400 mb-3 text-center">{[customer.service, customer.branch].filter(Boolean).join(' · ') || ' '}</div>}
            {customer?.medicalNote && <div className="text-[11px] text-red-300 mb-2 text-center">⚠ {customer.medicalNote}</div>}
            {customer?.doNotCall && <div className="text-[11px] text-red-300 mb-2 text-center">⛔ Đang chặn gọi</div>}
            {pendingCb && <div className="text-[11px] text-amber-300 mb-3 text-center">Hẹn gọi lại: {pendingCb.note || dayjs(pendingCb.dueAt).format('HH:mm DD/MM')}</div>}
            <div className={`w-28 h-28 rounded-full border-2 ${ses.phase === 'connected' ? 'border-green-500' : 'border-gray-600 animate-pulse'} flex items-center justify-center mb-5`}>
              <span className={`text-2xl font-mono ${ses.phase === 'connected' ? 'text-green-400' : 'text-gray-500'}`}>{fmtDuration(talkSeconds(ses))}</span>
            </div>
            {ses.phase === 'ringing' ? (
              <div className="flex gap-8">
                <button type="button" onClick={() => softphone.reject()} className="w-12 h-12 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center cursor-pointer" title="Từ chối"><PhoneOff size={20} /></button>
                <button type="button" onClick={() => softphone.answer()} className="w-12 h-12 rounded-full bg-green-500 hover:bg-green-600 flex items-center justify-center cursor-pointer animate-bounce" title="Nghe máy"><Phone size={20} /></button>
              </div>
            ) : (
              <div className="flex items-center gap-5">
                {live && ses.phase === 'connected' && (
                  <button type="button" onClick={() => softphone.toggleMute()} className={`w-10 h-10 rounded-full flex items-center justify-center cursor-pointer ${ses.muted ? 'bg-amber-500' : 'bg-gray-700 hover:bg-gray-600'}`} title={ses.muted ? 'Bật micro' : 'Tắt micro'}>
                    {ses.muted ? <MicOff size={16} /> : <Mic size={16} />}
                  </button>
                )}
                <button type="button" onClick={() => softphone.hangup()} className="w-12 h-12 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center cursor-pointer" title="Kết thúc cuộc gọi">
                  <PhoneOff size={20} />
                </button>
                {live && ses.phase === 'connected' && (
                  <button type="button" onClick={() => softphone.toggleHold()} className={`w-10 h-10 rounded-full flex items-center justify-center cursor-pointer ${ses.hold ? 'bg-amber-500' : 'bg-gray-700 hover:bg-gray-600'}`} title={ses.hold ? 'Tiếp tục' : 'Giữ máy'}>
                    {ses.hold ? <Play size={16} /> : <Pause size={16} />}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      <WrapupModal />
    </>
  );
}
